import type { BoardDoc } from '../../doc/BoardDoc';
import type { Rect } from '../../doc/geometry';
import type { SheetItem, SheetLayerDef } from '../../doc/schema';
import { enqueueUpload } from '../../sync/uploadQueue';
import { getAssetBlob, storeCanvasAsset } from '../assets/localAssets';
import { clipRect, TILE } from './brushes';

/**
 * Live pixels of sketch sheets. A layer being painted is a real canvas (`pxW × pxH`); every
 * other layer is shown from its asset. Strokes change the canvas at once; a debounced commit
 * encodes it to a PNG asset and points the layer at it (synced to other devices). Paint undo
 * is a per-sheet history of the tiles each stroke touched, separate from the board's undo.
 *
 * One store per open document, shared by both panes of a split view.
 */

export const COMMIT_DELAY_MS = 1500;
const MAX_UNDO_STEPS = 40;
const MAX_UNDO_BYTES = 120 * 1024 * 1024;
/** Retired live layers kept (so switching back is instant and the swap to an <img> is rare). */
const KEEP_RETIRED = 2;

export interface LiveLayer {
  key: string;
  sheetId: string;
  layerId: string;
  canvas: HTMLCanvasElement;
  /** Paint brushes draw here first (same size as the canvas). Allocated for the active layer. */
  scratch: HTMLCanvasElement | null;
  loaded: boolean;
  /** The layer's asset could not be fetched (offline, not cached): painting is refused. */
  unavailable: boolean;
  /** Pixels changed since the last commit. */
  dirty: boolean;
  /** The asset the canvas was loaded from or last committed to (null: empty). */
  assetId: string | null;
  /** No longer the active layer: dropped once its commit lands and it falls out of the LRU. */
  retired: boolean;
  lastUsed: number;
}

interface UndoTile {
  x: number;
  y: number;
  data: ImageData;
}

interface UndoEntry {
  layerId: string;
  tiles: Map<string, UndoTile>;
  bytes: number;
  at: number;
}

interface SheetHistory {
  undo: UndoEntry[];
  redo: UndoEntry[];
}

const key = (sheetId: string, layerId: string) => `${sheetId}:${layerId}`;

export class RasterStore {
  private live = new Map<string, LiveLayer>();
  private history = new Map<string, SheetHistory>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private committing = new Map<string, Promise<void>>();
  private listeners = new Set<() => void>();
  private version = 0;
  /** The stroke being recorded, per layer key. */
  private recording = new Map<string, UndoEntry>();
  private unsubscribe: () => void;

  constructor(readonly board: BoardDoc) {
    // A layer's asset changed under a live canvas (another device, a board undo of a clear or
    // merge, a deleted layer): reload it unless local strokes are waiting to be committed.
    this.unsubscribe = board.index.subscribe(() => {
      for (const l of [...this.live.values()]) {
        const def = this.layerDef(l.sheetId, l.layerId);
        if (!def) {
          this.drop(l);
          continue;
        }
        if (def.assetId !== l.assetId && !l.dirty && !this.committing.has(l.key)) void this.load(l, def.assetId);
      }
    });
  }

  // ---- subscription (SheetItem bodies and the panels re-render on change) ----------------

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getVersion = (): number => this.version;

  private bump(): void {
    this.version++;
    for (const l of this.listeners) l();
  }

  // ---- lookups ---------------------------------------------------------------------------

  sheet(sheetId: string): SheetItem | null {
    const item = this.board.index.get(sheetId);
    return item && item.type === 'sheet' ? item : null;
  }

  layerDef(sheetId: string, layerId: string): SheetLayerDef | null {
    return this.sheet(sheetId)?.props.layers[layerId] ?? null;
  }

  liveLayer(sheetId: string, layerId: string): LiveLayer | undefined {
    return this.live.get(key(sheetId, layerId));
  }

  /** Every live layer of a sheet (active and retired). */
  liveLayersOf(sheetId: string): LiveLayer[] {
    return [...this.live.values()].filter((l) => l.sheetId === sheetId);
  }

  /** Steps the paint undo/redo of a sheet can take. */
  canUndo(sheetId: string): boolean {
    return (this.history.get(sheetId)?.undo.length ?? 0) > 0;
  }

  canRedo(sheetId: string): boolean {
    return (this.history.get(sheetId)?.redo.length ?? 0) > 0;
  }

  /** When the newest paint step of the sheet was made (to compare with the board's undo). */
  lastUndoAt(sheetId: string): number {
    const h = this.history.get(sheetId);
    return h?.undo[h.undo.length - 1]?.at ?? 0;
  }

  /** When the newest undone paint step was undone (to compare with the board's redo). */
  lastRedoAt(sheetId: string): number {
    const h = this.history.get(sheetId);
    return h?.redo[h.redo.length - 1]?.at ?? 0;
  }

  // ---- activation ------------------------------------------------------------------------

  /**
   * Makes a layer live (loading its asset) and the only active one: other live layers of the
   * board are committed and retired. Resolves once the canvas holds the layer's pixels.
   */
  async activate(sheetId: string, layerId: string): Promise<LiveLayer | null> {
    const sheet = this.sheet(sheetId);
    const def = sheet?.props.layers[layerId];
    if (!sheet || !def) return null;
    const k = key(sheetId, layerId);
    let l = this.live.get(k);
    for (const other of this.live.values()) {
      if (other.key !== k && !other.retired) this.retire(other);
    }
    if (!l) {
      const canvas = document.createElement('canvas');
      canvas.width = sheet.props.pxW;
      canvas.height = sheet.props.pxH;
      // the undo backs tiles up with getImageData before every stroke: keep the pixels CPU-side
      canvas.getContext('2d', { willReadFrequently: true });
      l = { key: k, sheetId, layerId, canvas, scratch: null, loaded: false, unavailable: false, dirty: false, assetId: def.assetId, retired: false, lastUsed: Date.now() };
      this.live.set(k, l);
      this.bump();
      await this.load(l, def.assetId);
    } else {
      l.retired = false;
      l.lastUsed = Date.now();
    }
    if (!l.scratch) {
      const scratch = document.createElement('canvas');
      scratch.width = l.canvas.width;
      scratch.height = l.canvas.height;
      l.scratch = scratch;
    }
    this.pruneRetired();
    this.bump();
    return l;
  }

  /** The active (not retired, loaded) live layer of a sheet, if any. */
  active(sheetId: string): LiveLayer | null {
    for (const l of this.live.values()) if (l.sheetId === sheetId && !l.retired) return l;
    return null;
  }

  /** Leaves paint mode on a sheet: commits what is pending and retires its layers. */
  deactivate(sheetId?: string): void {
    for (const l of this.live.values()) {
      if (sheetId && l.sheetId !== sheetId) continue;
      if (!l.retired) this.retire(l);
    }
    this.pruneRetired();
    this.bump();
  }

  private retire(l: LiveLayer): void {
    l.retired = true;
    l.scratch = null;
    if (l.dirty) void this.commit(l);
  }

  /** Drops retired layers beyond the LRU budget (never one with pixels still to commit). */
  private pruneRetired(): void {
    const retired = [...this.live.values()].filter((l) => l.retired && !l.dirty && !this.committing.has(l.key)).sort((a, b) => b.lastUsed - a.lastUsed);
    for (const l of retired.slice(KEEP_RETIRED)) this.drop(l);
  }

  private drop(l: LiveLayer): void {
    const t = this.timers.get(l.key);
    if (t) clearTimeout(t);
    this.timers.delete(l.key);
    this.live.delete(l.key);
    l.canvas.width = 0;
    l.canvas.height = 0;
    this.bump();
  }

  private async load(l: LiveLayer, assetId: string | null): Promise<void> {
    l.loaded = false;
    l.unavailable = false;
    l.assetId = assetId;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, l.canvas.width, l.canvas.height);
    if (assetId) {
      let ok = false;
      const blob = await getAssetBlob(assetId, 'original');
      // the layer may have changed again while the blob was fetched
      if (l.assetId !== assetId || !this.live.has(l.key)) return;
      if (blob) {
        try {
          const bmp = await createImageBitmap(blob);
          if (l.assetId === assetId && this.live.has(l.key)) {
            ctx.drawImage(bmp, 0, 0, l.canvas.width, l.canvas.height);
            ok = true;
          }
          bmp.close();
        } catch (e) {
          console.warn('[paint] layer asset could not be decoded', assetId, e);
        }
      }
      if (!ok) {
        // never let a blank canvas overwrite pixels that only failed to arrive
        l.unavailable = true;
        this.bump();
        return;
      }
    }
    l.loaded = true;
    this.bump();
  }

  // ---- strokes and undo ------------------------------------------------------------------

  /** Starts recording a stroke's undo entry on a layer. */
  beginStroke(l: LiveLayer): (rect: Rect) => void {
    const entry: UndoEntry = { layerId: l.layerId, tiles: new Map(), bytes: 0, at: Date.now() };
    this.recording.set(l.key, entry);
    return (rect) => this.backupTiles(l, entry, rect);
  }

  private backupTiles(l: LiveLayer, entry: UndoEntry, rect: Rect): void {
    const r = clipRect(rect, l.canvas.width, l.canvas.height);
    if (!r) return;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return;
    const tx0 = Math.floor(r.x / TILE);
    const ty0 = Math.floor(r.y / TILE);
    const tx1 = Math.floor((r.x + r.w - 1) / TILE);
    const ty1 = Math.floor((r.y + r.h - 1) / TILE);
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const id = `${tx},${ty}`;
        if (entry.tiles.has(id)) continue;
        const x = tx * TILE;
        const y = ty * TILE;
        const w = Math.min(TILE, l.canvas.width - x);
        const h = Math.min(TILE, l.canvas.height - y);
        if (w <= 0 || h <= 0) continue;
        const data = ctx.getImageData(x, y, w, h);
        entry.tiles.set(id, { x, y, data });
        entry.bytes += data.data.byteLength;
      }
    }
  }

  /** The stroke is on the canvas: keep its undo entry, mark the layer dirty, schedule a commit. */
  endStroke(l: LiveLayer, changed: Rect | null): void {
    const entry = this.recording.get(l.key);
    this.recording.delete(l.key);
    if (!changed || !entry || !entry.tiles.size) return;
    const h = this.historyOf(l.sheetId);
    h.undo.push(entry);
    h.redo.length = 0;
    this.trimHistory(h);
    this.touched(l);
  }

  /** A stroke was abandoned (a second finger landed): put the backed-up tiles back. */
  cancelStroke(l: LiveLayer): void {
    const entry = this.recording.get(l.key);
    this.recording.delete(l.key);
    if (!entry) return;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return;
    for (const t of entry.tiles.values()) ctx.putImageData(t.data, t.x, t.y);
    this.bump();
  }

  /** Pixels changed outside a stroke session (fill): schedule the commit. */
  touched(l: LiveLayer): void {
    l.dirty = true;
    l.lastUsed = Date.now();
    this.scheduleCommit(l);
    this.bump();
  }

  private historyOf(sheetId: string): SheetHistory {
    let h = this.history.get(sheetId);
    if (!h) {
      h = { undo: [], redo: [] };
      this.history.set(sheetId, h);
    }
    return h;
  }

  private trimHistory(h: SheetHistory): void {
    let bytes = h.undo.reduce((s, e) => s + e.bytes, 0) + h.redo.reduce((s, e) => s + e.bytes, 0);
    while (h.undo.length && (h.undo.length > MAX_UNDO_STEPS || bytes > MAX_UNDO_BYTES)) {
      bytes -= h.undo.shift()!.bytes;
    }
  }

  /** Swaps an entry's tiles with the canvas (undo and redo are the same operation). */
  private async applyEntry(sheetId: string, entry: UndoEntry): Promise<UndoEntry | null> {
    const l = await this.activate(sheetId, entry.layerId);
    if (!l || !l.loaded) return null;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return null;
    const inverse: UndoEntry = { layerId: entry.layerId, tiles: new Map(), bytes: 0, at: Date.now() };
    for (const [id, t] of entry.tiles) {
      const now = ctx.getImageData(t.x, t.y, t.data.width, t.data.height);
      inverse.tiles.set(id, { x: t.x, y: t.y, data: now });
      inverse.bytes += now.data.byteLength;
      ctx.putImageData(t.data, t.x, t.y);
    }
    this.touched(l);
    return inverse;
  }

  /** Undoes the newest paint step of the sheet (activating its layer if needed). */
  async undo(sheetId: string): Promise<boolean> {
    const h = this.historyOf(sheetId);
    const entry = h.undo.pop();
    if (!entry) return false;
    const inverse = await this.applyEntry(sheetId, entry);
    if (inverse) h.redo.push(inverse);
    this.bump();
    return true;
  }

  async redo(sheetId: string): Promise<boolean> {
    const h = this.historyOf(sheetId);
    const entry = h.redo.pop();
    if (!entry) return false;
    const inverse = await this.applyEntry(sheetId, entry);
    if (inverse) h.undo.push(inverse);
    this.bump();
    return true;
  }

  /** Forgets the paint history of a sheet (its layer structure changed under it). */
  clearHistory(sheetId: string, layerId?: string): void {
    const h = this.history.get(sheetId);
    if (!h) return;
    if (!layerId) {
      h.undo.length = 0;
      h.redo.length = 0;
    } else {
      h.undo = h.undo.filter((e) => e.layerId !== layerId);
      h.redo = h.redo.filter((e) => e.layerId !== layerId);
    }
    this.bump();
  }

  // ---- commits ---------------------------------------------------------------------------

  private scheduleCommit(l: LiveLayer): void {
    const t = this.timers.get(l.key);
    if (t) clearTimeout(t);
    this.timers.set(
      l.key,
      setTimeout(() => {
        this.timers.delete(l.key);
        void this.commit(l);
      }, COMMIT_DELAY_MS),
    );
  }

  /** Encodes the layer and points the document at the new asset. Safe to call any time. */
  commit(l: LiveLayer): Promise<void> {
    const t = this.timers.get(l.key);
    if (t) clearTimeout(t);
    this.timers.delete(l.key);
    const running = this.committing.get(l.key);
    if (running) return running;
    if (!l.dirty || !l.loaded) return Promise.resolve();
    const p = (async () => {
      // toBlob snapshots the bitmap synchronously; strokes landing meanwhile mark it dirty again
      l.dirty = false;
      try {
        const meta = await storeCanvasAsset(l.canvas, this.board.boardId, `sheet-${l.layerId.slice(0, 8)}.png`);
        if (!this.live.has(l.key)) return;
        l.assetId = meta.id;
        if (this.layerDef(l.sheetId, l.layerId)) {
          this.board.setSheetLayerAsset(l.sheetId, l.layerId, meta.id);
          if (this.board.remote) void enqueueUpload(this.board.boardId, meta.id);
        }
      } catch (e) {
        console.warn('[paint] layer not saved, will retry', e);
        l.dirty = true;
      }
    })().finally(() => {
      this.committing.delete(l.key);
      if (l.dirty && this.live.has(l.key)) this.scheduleCommit(l);
      else if (l.retired) this.pruneRetired();
      this.bump();
    });
    this.committing.set(l.key, p);
    return p;
  }

  /** Commits every dirty layer now (the tab is going to the background). */
  flush(): Promise<void> {
    return Promise.all([...this.live.values()].filter((l) => l.dirty).map((l) => this.commit(l))).then(() => undefined);
  }

  /** Is a commit or a load in flight for the sheet (the panel shows a saving hint)? */
  busy(sheetId: string): boolean {
    for (const l of this.live.values()) if (l.sheetId === sheetId && (this.committing.has(l.key) || l.dirty || !l.loaded)) return true;
    return false;
  }

  destroy(): void {
    this.unsubscribe();
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    for (const l of this.live.values()) {
      l.canvas.width = 0;
      l.canvas.height = 0;
    }
    this.live.clear();
    this.history.clear();
    this.listeners.clear();
  }
}

// ---- one store per document --------------------------------------------------------------

const stores = new WeakMap<BoardDoc, RasterStore>();

export function rasterStore(board: BoardDoc): RasterStore {
  let s = stores.get(board);
  if (!s) {
    s = new RasterStore(board);
    stores.set(board, s);
  }
  return s;
}
